import React from 'react';
import Box from '@mui/material/Box';
import { Link as RouterLink, generatePath } from 'react-router-dom';
import Link from '@mui/material/Link';
import ContributionBar, {
  EmptyContributionBar,
} from '@/dashboard/components/ContributionBar/ContributionBar';
import ContributionMetaStatus from '@/dashboard/components/ContributionMeta/ContributionMetaStatus';
import ContributionMetaTotals from '@/dashboard/components/ContributionMeta/ContributionMetaTotals';
import { useTablet } from '@/hooks/responsiveHooks';
import { useDashboardCountsContext } from '@/dashboard/provider/DashboardCountsProvider';
import { ROUTE_NAMES } from '@/core/routes/routeNames';
import Button from '@/components/Button/Button';
import BypassApprovalDate from '@/dashboard/components/BypassSubmission/BypassApprovedDate';
import { hasPermission } from '@/authorization/AuthorizationGate/AuthorizationGate';
import { SCOPES } from '@/authorization/authorization.constants';
import ValidateBatchButtonLoader from '@/dashboard/components/ValidateBatchButtonLoader/ValidateBatchButtonLoader';
import SubmitButtonLoader from '@/dashboard/components/SubmitButtonLoader/SubmitButtonLoader';

interface DashboardRowProps {
  readableMonth: string;
  numericMonth: string;
}

function DashboardRow({ readableMonth, numericMonth }: DashboardRowProps) {
  const isTablet = useTablet();
  const { counts, contributionCountByPeriod, bandDetails } = useDashboardCountsContext();
  const periodBandDetails = bandDetails[numericMonth];
  const employerId = counts?.[numericMonth]?.employerId;
  const totalEmployees = counts?.[numericMonth]?.totals?.employeeRecords;
  const bypassApprovalDate = counts[numericMonth].bypassTimestamp;

  if (!employerId || totalEmployees === undefined || periodBandDetails === undefined) {
    throw new Error(`employerId/totalEmployees/period status info missing for ${numericMonth}`);
  }

  const isEmptyPeriod = totalEmployees === 0;
  const periodLabel = periodBandDetails.label;
  const isPeriodWithoutContribution =
    contributionCountByPeriod && numericMonth in contributionCountByPeriod
      ? contributionCountByPeriod[numericMonth] === 0
      : true;

  let bgColour = 'transparent';
  if (periodLabel === 'Attention required' || periodBandDetails.bands.unsubmitted > 0) {
    bgColour = 'custom.errorFill';
  }

  return (
    <Box
      component="li"
      display="flex"
      flexDirection="column"
      flexWrap="nowrap"
      gap={1}
      px={4}
      pt={4}
      pb={2}
      borderRadius={0.75}
      bgcolor={bgColour}
      minWidth={500}
    >
      <Box display="flex" alignItems="center" gap={6}>
        <Link
          component={RouterLink}
          to={generatePath(ROUTE_NAMES.DASHBOARD.PAYROLL_PERIOD.FROM_DASHBOARD, {
            year: numericMonth.substring(0, 4),
            month: numericMonth.substring(4),
          })}
          title={`Contributions from ${readableMonth}`}
          variant="h5"
          fontWeight="600"
          display="flex"
          flexShrink="0"
          flexBasis={isTablet ? '190px' : '250px'}
          color="inherit"
        >
          {readableMonth}
        </Link>
        <UploadButtonLoader
          period={numericMonth}
          readableMonth={readableMonth}
          visibility={periodBandDetails.buttons.upload}
        />
        {isEmptyPeriod ? (
          <EmptyContributionBar />
        ) : (
          <>
            <ContributionBar period={numericMonth} />
            {!isPeriodWithoutContribution ? (
              <>
                <ValidateBatchButtonLoader
                  readableMonth={readableMonth}
                  employerId={employerId}
                  visibility={periodBandDetails.buttons.validate}
                />
                <SubmitButtonLoader
                  numericMonth={numericMonth}
                  readableMonth={readableMonth}
                  visibility={periodBandDetails.buttons.submit}
                />
              </>
            ) : null}
          </>
        )}
      </Box>
      <Box display="flex" alignItems="flex-start" justifyContent="flex-start" gap={6}>
        <Box
          flexShrink="0"
          flexBasis={isTablet ? '190px' : '250px'}
          display="flex"
          flexDirection="column"
          gap={0.5}
        >
          <ContributionMetaStatus period={numericMonth} />
          {bypassApprovalDate ? (
            <BypassApprovalDate period={numericMonth} bypassApprovalDate={bypassApprovalDate} />
          ) : null}
        </Box>
        <ContributionMetaTotals period={numericMonth} />
      </Box>
    </Box>
  );
}

type UploadButtonLoaderProps = {
  period: string;
  readableMonth: string;
  visibility: boolean | null;
};
function UploadButtonLoader({ period, readableMonth, visibility }: UploadButtonLoaderProps) {
  const label = `Upload payroll file for ${readableMonth}`;
  const canUploadBatch = hasPermission([SCOPES.UPLOAD_BATCH]);

  return visibility === null || visibility === false ? (
    <></>
  ) : (
    <Button
      component={RouterLink}
      aria-label={label}
      to={generatePath(ROUTE_NAMES.DASHBOARD.IMPORT_PAYROLL.FULL, {
        period,
      })}
      variant="contained"
      title={label}
      disabled={!canUploadBatch}
    >
      Upload
    </Button>
  );
}

export default DashboardRow;
